// Client-side authentication functions
import { getAuthToken, setAuthToken, setUser, clearAuth, isAuthenticated } from '@/lib/auth-utils';
const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL;

type ApiMessage = {
  message?: string;
  errors?: Record<string, string[]>;
};

async function readApiMessage(response: Response, fallback: string): Promise<string> {
  const data = await response.json().catch(() => ({})) as ApiMessage;
  const validationMessage = Object.values(data.errors ?? {}).flat()[0];

  return validationMessage ?? data.message ?? fallback;
}

export async function requestPasswordReset(email: string): Promise<string> {
  const response = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
    },
    body: JSON.stringify({ email }),
  });

  const message = await readApiMessage(response, 'Unable to request a password reset link.');
  if (!response.ok) throw new Error(message);

  return message;
}

export async function resetPassword(payload: {
  email: string;
  token: string;
  password: string;
  password_confirmation: string;
}): Promise<string> {
  const response = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
    },
    body: JSON.stringify(payload),
  });

  const message = await readApiMessage(response, 'Unable to reset your password.');
  if (!response.ok) throw new Error(message);

  return message;
}

// Function to handle login using localStorage
export async function login(email: string, password: string) {
  try {
    console.log('Attempting login for email:', email);

    const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache'
      },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      console.error('Login failed with status:', response.status);
      throw new Error('Login failed');
    }

    const data = await response.json();
    // The organization name comes next to `user`, not inside it
    const { token, user, organization_name: organizationName = null } = data;

    console.log('Server login response:', { user });

    // Store token and user data in localStorage
    setAuthToken(token);
    setUser(user);

    return { token, user, organizationName };
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
}

// Export utility functions for convenience
export { getAuthToken, setAuthToken, setUser, clearAuth as logout, isAuthenticated } from '@/lib/auth-utils';

// Get user data - re-export for compatibility
export function getUser(): any | null {
  if (typeof window !== 'undefined') {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }
  return null;
}
